import type { EventDto, EventOrganizer, ItemReference } from '@/types'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { ComputedRef, Ref } from 'vue'
import { computed, ref } from 'vue'
import { resolveUnref } from '@vueuse/core'
import { createEvent, deleteEventById, apiSoftDeleteEvent, findDuplicateCandidates, getEventById, getEventsForUser, apiPublishEvent, apiUnpublishEvent, updateEvent, type EventImageUpdateMode } from '@/api'
import { isAxiosError } from 'axios'
import { useAppConfigurationStore } from '@/stores/useAppConfigurationStore'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useUserStore } from '@/stores/userStore'

export interface UseEventsStore {
    allEvents: Ref<EventDto[]>
    savedEvents: ComputedRef<EventDto[]>
    activeEvents: ComputedRef<EventDto[]>
    endedEvents: ComputedRef<EventDto[]>

    getEvent(id: number): Promise<EventDto>
    deleteEvent(ev: EventDto): Promise<void>
    deleteEndedEvent(ev: EventDto): Promise<void>
    saveNewEvent(ev: EventDto, image?: File, imageUpdateMode?: EventImageUpdateMode, originalImage?: File): Promise<EventDto>
    duplicateEvent(ev: EventDto): Promise<void>
    beginNewEventCreation(): void
    publishEvent(ev: EventDto): Promise<void>
    unpublishEvent(ev: EventDto): Promise<void>
    getAllEventsForOrganizers(organizers: ItemReference[]): Promise<void>
}

export const useEventStore = defineStore<'eventStore', UseEventsStore>('eventStore', function (): UseEventsStore {
    const appConfigurationStore = useAppConfigurationStore()
    const userStore = useUserStore()
    const authStore = useAuthenticationStore()
    const allEvents = ref<EventDto[]>([])
    const lastLoadedOrganizersKey = ref('')
    let loadEventsPromise: Promise<void> | null = null
    let loadingOrganizersKey = ''

    function _byStartAsc(a: EventDto, b: EventDto) {
        const ta = a.start ? new Date(a.start).getTime() : Infinity
        const tb = b.start ? new Date(b.start).getTime() : Infinity
        return ta - tb
    }

    const savedEvents = computed<EventDto[]>(() => {
        return allEvents.value.filter((x) => !_eventIsPublished(x)).sort(_byStartAsc)
    })
    const activeEvents = computed<EventDto[]>(() => {
        return allEvents.value.filter((x) => _eventIsPublished(x) && !_eventIsEnded(x)).sort(_byStartAsc)
    })
    const endedEvents = computed<EventDto[]>(() => {
        return allEvents.value.filter((x) => _eventIsPublished(x) && _eventIsEnded(x)).sort(_byStartAsc)
    })

    function _eventIsPublished(ev: EventDto) {
        return ev.publish != null && ev.publish < new Date()
    }

    function _eventIsEnded(ev: EventDto) {
        return ev.end != null && ev.end < new Date()
    }

    async function getAllEventsForOrganizers(organizers: ItemReference[], force = false) {
        const organizersKey = organizers
            .map((organizer) => `${organizer.partitionKey}:${organizer.id}`)
            .sort()
            .join('|')
        const userId = userStore.user.id
        if (!userId) {
            allEvents.value = []
            lastLoadedOrganizersKey.value = ''
            return
        }

        const loadKey = `${userId}:${organizersKey}`

        if (!force && loadKey === lastLoadedOrganizersKey.value) {
            return
        }

        if (!force && loadEventsPromise && loadingOrganizersKey === loadKey) {
            await loadEventsPromise
            return
        }

        loadingOrganizersKey = loadKey
        loadEventsPromise = (async () => {
            let events = await getEventsForUser(userId)

            if (appConfigurationStore.isTidTilTur()) {
                events = events.filter((ev) => ev.typeId === 1000)
            } else if (appConfigurationStore.isAffaldsindsamlingen()) {
                events = events.filter(ev => ev.typeId >= 2000 && ev.typeId <= 2007)
            }

            allEvents.value = events
            lastLoadedOrganizersKey.value = loadKey
        })()

        try {
            await loadEventsPromise
        } finally {
            loadEventsPromise = null
            loadingOrganizersKey = ''
        }
    }
    async function getEvent(id: number) {
        var event = await getEventById(id)

        return event
    }

    async function deleteEvent(ev: EventDto) {
        try {
            await deleteEventById(ev.id, ev.partitionKey)
        } catch (error) {
            // A second delete is harmless: another click/request may already
            // have removed the event.
            if (!isAxiosError(error) || error.response?.status !== 404) {
                throw error
            }
        }
        allEvents.value = allEvents.value.filter((x) => !(x.id === ev.id))
    }

    async function deleteEndedEvent(ev: EventDto) {
        await apiSoftDeleteEvent(ev.id, ev.partitionKey)
        allEvents.value = allEvents.value.filter((x) => !(x.id === ev.id))
    }

    let createIdempotencyKey: string | null = null

    function beginNewEventCreation() {
        createIdempotencyKey = null
    }

    function nextCreateIdempotencyKey(forceNew = false) {
        if (forceNew || !createIdempotencyKey) {
            createIdempotencyKey = crypto.randomUUID()
        }
        return createIdempotencyKey
    }

    async function confirmSemanticDuplicates(ev: EventDto) {
        try {
            const candidates = await findDuplicateCandidates(ev)
            if (candidates.length === 0 || typeof window === 'undefined') {
                return
            }

            const lines = candidates
                .map(candidate => `#${candidate.id} ${candidate.title ?? ''} ${candidate.start ?? ''}`.trim())
                .join('\n')
            const confirmed = window.confirm(
                `Der findes allerede lignende arrangementer:\n${lines}\n\nVil du oprette et nyt alligevel?`
            )
            if (!confirmed) {
                throw new Error('Oprettelse blev annulleret, fordi der allerede findes et lignende arrangement.')
            }
        } catch (error) {
            if (error instanceof Error && error.message.startsWith('Oprettelse blev annulleret')) {
                throw error
            }
        }
    }

    async function saveNewEvent(ev: EventDto, image?: File, imageUpdateMode: EventImageUpdateMode = 'keepHistory', originalImage?: File): Promise<EventDto> {
        const newEvent = ev.id === 0
            ? await createConfirmedEvent(ev, image, originalImage)
            : await updateEvent(ev, image, imageUpdateMode, originalImage)
        if (!ev.useSignupInfo) {
            newEvent.signupInfo = ''
        }
        const newEventIdx = allEvents.value.findIndex((x) => newEvent.id === x.id)
        if (newEventIdx === -1) {
            allEvents.value.push(newEvent)
        } else {
            Object.assign(allEvents.value[newEventIdx], newEvent)
        }
        return newEvent
    }

    async function publishEvent(ev: EventDto){
        const publisherEmail =
            userStore.user.email?.trim()
            || userStore.user.contact?.emailAddress?.trim()
            || authStore.userEmail?.trim()
            || authStore.account?.username?.trim()
            || ''

        await apiPublishEvent(ev, userStore.user.id, publisherEmail)
        
        // Update the local event with the publish date
        const eventIdx = allEvents.value.findIndex((x) => x.id === ev.id)
        if (eventIdx !== -1) {
            allEvents.value[eventIdx].publish = new Date()
        }
    }

    async function unpublishEvent(ev: EventDto) {
        const unpublishedEvent = await apiUnpublishEvent(ev)
        const eventIdx = allEvents.value.findIndex((x) => x.id === ev.id)

        if (eventIdx !== -1) {
            Object.assign(allEvents.value[eventIdx], unpublishedEvent)
        }
    }

    async function createConfirmedEvent(ev: EventDto, image?: File, originalImage?: File) {
        const idempotencyKey = nextCreateIdempotencyKey()
        await confirmSemanticDuplicates(ev)
        const created = await createEvent(ev, image, originalImage, idempotencyKey)
        createIdempotencyKey = null
        return created
    }

    async function duplicateEvent(ev: EventDto) {
        const clone: EventDto = JSON.parse(JSON.stringify(resolveUnref(ev)))
        clone.id = 0
        clone.publish = undefined
        clone.cancelledAt = undefined
        clone.cancellationReason = ''
        clone.occupied = false
        clone.occupiedUpdated = undefined
        clone.occupiedUpdatedBy = undefined
        beginNewEventCreation()
        await saveNewEvent(clone)
    }

    return {
        allEvents,
        savedEvents,
        activeEvents,
        endedEvents,
        getEvent,
        deleteEvent,
        deleteEndedEvent,
        saveNewEvent,
        duplicateEvent,
        beginNewEventCreation,
        publishEvent,
        unpublishEvent,
        getAllEventsForOrganizers
    }
})
// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot))
}
